<template>
  <div class="asTextarea">
    <label
      v-if="label"
      class="asTextarea_label"
    >
      {{label}}
      <small v-if="modelValue.length > 0 && maxlength > 0">({{`${modelValue.length} / ${maxlength}`}})</small>
    </label>

    <div
      v-if="autoResizable"
      ref="inputRef"
      class="asTextarea_input"
      :class="{
        '-isAutoResizable': autoResizable
      }"
      contenteditable
      @input="input"
      :autofocus="autofocus"
      @click="triggerFocus"
    />

    <textarea
      v-else
      ref="inputRef"
      class="asTextarea_input"
      :value="modelValue"
      :placeholder="placeholder"
      :maxlength="maxlength"
      @input="input"
      :autofocus="autofocus"
    />

    <span v-if="error" class="asTextarea_error">{{ error }}</span>
    <span v-else-if="note" class="asTextarea_note">{{ note }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'

export default defineComponent({
  name: 'AsTextArea',
  props: {
    name: {
      type: String
    },
    label: {
      type: String
    },
    placeholder: {
      type: String,
      default: ''
    },
    modelValue: {
      type: [String, Number],
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    note: {
      type: String,
      default: ''
    },
    appendIcon: {
      type: String,
      default: ''
    },
    maxlength: {
      type: [String, Number],
      default: ''
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    autoResizable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'update:modelValue'],
  setup (props, ctx) {
    const inputRef = ref<HTMLInputElement | null>(null)

    function input (event) {
      if (event.target.innerHTML.trim() === '<br>') event.target.innerHTML = ''
      if (inputRef.value) {
        inputRef.value.setAttribute('value', event.target.value || event.target.innerText)
      }
      ctx.emit('update:modelValue', event.target.value || event.target.innerText)
    }

    function triggerFocus (e) {
      e.target.focus()
    }

    onMounted(() => {
      if (inputRef.value) {
        inputRef.value.setAttribute('value', props.modelValue.toString())
      }
    })

    return {
      inputRef,
      input,
      triggerFocus
    }
  }
})
</script>

<style lang="scss">
  .asTextarea {
    position: relative;

    width: 100%;
    max-width: 342px;
    margin-top: 16px;

    &.-fullWidth {
      max-width: 100% !important;
    }

    &:first-child {
      margin-top: 0;
    }

    &_label {
      display: flex;

      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      line-height: 20px;

      small {
        margin-left: auto;
      }
    }

    &.-black &_label {
      color: rgba(0, 0, 0, 0.7);
    }

    &_input {
      display: block;
      width: 100%;
      height: 22px * 6;
      margin-bottom: 1px;

      background-color: transparent;
      border: 0;
      outline: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.3);

      color: #ffffff;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: 0.3px;

      transition: color .3s;
      resize: none;

      &.-isAutoResizable {
        height: auto;
        max-height: 132px;
        overflow-y: auto;
        overflow-x: hidden;
        cursor: text;

        &:empty::after {
          content: attr(placeholder);
          color: rgba(76, 82, 100, 0.7);
        }
      }

      &.-value,
      &:not([value=""]) {
        border-bottom: 1px solid rgb(255, 255, 255);
      }

      &:focus {
        border-bottom: 2px solid #ffffff;
        margin-bottom: 0;
      }

      &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
        color: currentColor;
      }

      &::-moz-placeholder { /* Firefox 19+ */
        color: currentColor;
      }

      &:-ms-input-placeholder { /* IE 10+ */
        color: currentColor;
      }

      &::placeholder {
        color: currentColor;
      }
    }

    &.-black &_input {
      color: #000;
      border-bottom: 1px solid rgba(0, 0, 0, 0.3);

      &:focus {
        border-color: #000;
      }

      &.-value {
        border-color: #000;
      }

      &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
        color: #000;
      }

      &::-moz-placeholder { /* Firefox 19+ */
        color: #000;
      }

      &:-ms-input-placeholder { /* IE 10+ */
        color: #000;
      }

      &::placeholder {
        color: #000;
      }
    }

    &.-grey &_label {
      color: #4c5264;
    }
    &.-grey &_input {
      color: #4c5264;
      border-bottom: 1px solid rgba(0, 0, 0, 0.3);

      &:focus {
        border-color: #4c5264;
      }

      &.-value {
        border-color: #4c5264;
      }

      &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
        color: #4c5264;
      }

      &::-moz-placeholder { /* Firefox 19+ */
        color: #4c5264;
      }

      &:-ms-input-placeholder { /* IE 10+ */
        color: #4c5264;
      }

      &::placeholder {
        color: #4c5264;
      }
    }

    &_error {
      display: inline-block;
      padding-top: 5px;
      font-size: 12px;

      color: #ff746c;

      &:empty {
        display: none;
      }
    }

    &_note {
      display: inline-block;
      padding-top: 5px;
      font-size: 12px;

      color: #4c5264;

      &:empty {
        display: none;
      }
    }
  }
</style>
